import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, Card, Dialog, Field, Input, toast, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Truck } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  needsConfirmation,
  ProductionTransferClient,
  type ProductionOrderComponentDTO,
  type ProductionTransferDTO,
  type ProductionTransferStatus,
} from "../../../api/floor";
import { formatNumber, refusalMessage } from "../../../api/manufacturing";
import type { ProductionOrderDTO } from "../../../api/production";
import { ReceiveTransferDialog } from "../ReceiveTransferDialog";
import { today } from "./lotModel";

/** A production order ships while on the floor at Laval and after its first truck. */
const SHIPPABLE = new Set(["RELEASED", "PRODUCED", "SHIPPING"]);

function transferStatusVariant(status: ProductionTransferStatus) {
  return status === "RECEIVED" ? "success" : status === "CANCELLED" ? "neutral" : "warning";
}

/**
 * Trucks from Laval to Terrebonne: the bon de transfert as records. Ship what
 * is finished (prefilled with what each component can still ship), count it
 * on arrival, cancel a truck that never left. The complete sets on a truck
 * leave Laval's stock; the order is Shipping with the first truck and closes
 * with the one that takes its last set. What arrives firms the assembly
 * orders; what is short broke on the road.
 */
export function TransfersTab({
  lot,
  components,
  editable,
  onChanged,
}: {
  lot: ProductionOrderDTO;
  components: ProductionOrderComponentDTO[];
  editable: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const confirm = useConfirm();
  const client = useMemo(() => new ProductionTransferClient(http), [http]);
  const [shipping, setShipping] = useState(false);
  const [receiving, setReceiving] = useState<ProductionTransferDTO | null>(null);

  const transfers = useQuery({
    queryKey: ["production", "production-transfer", "lot", lot.id],
    queryFn: () => client.forLot(lot.id),
  });

  const cancel = useMutation({
    mutationFn: (transfer: ProductionTransferDTO) => client.cancel(transfer.id),
    onSuccess: async () => {
      await transfers.refetch();
      await onChanged();
      toast.success(t("lot.transfer.cancelled", "Transfer cancelled"));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const rows = transfers.data ?? [];
  const canShip = editable && SHIPPABLE.has(lot.status) && components.length > 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[13px] text-ink-muted">
          {t("lot.transfer.intro", "Parts go from Laval to Terrebonne by truck; each truck is counted when it arrives.")}
        </p>
        {canShip && (
          <Button variant="primary" className="ml-auto" onClick={() => setShipping(true)}>
            <Truck className="size-4" />
            {t("lot.transfer.ship", "Ship to Terrebonne")}
          </Button>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="text-[13px] text-ink-muted">
          {transfers.isLoading ? t("common.loading", "Loading…") : t("lot.transfer.none", "Nothing has left Laval for this lot yet.")}
        </p>
      ) : (
        rows.map((transfer) => (
          <Card key={transfer.id} className="p-4 sm:p-5">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="text-[14px] font-semibold">{t("lot.transfer.title", "Transfer {{n}}", { n: transfer.number })}</span>
              <Badge variant={transferStatusVariant(transfer.status)} size="sm">
                {transferStatusLabel(t, transfer.status)}
              </Badge>
              <span className="text-[12px] text-ink-muted">
                {[transfer.shipped_date, transfer.truck_reference, `${transfer.from_site_code ?? ""} → ${transfer.to_site_code ?? ""}`]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
              {editable && transfer.status === "SHIPPED" && (
                <span className="ml-auto flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={cancel.isPending}
                    onClick={async () => {
                      if (
                        await confirm({
                          title: t("lot.transfer.cancel.title", "Cancel this transfer?"),
                          message: t("lot.transfer.cancel.message", "Only for a truck that never left. The lot goes back to where it was if no other truck is on file."),
                          confirmLabel: t("lot.transfer.cancel", "Cancel transfer"),
                          variant: "danger",
                        })
                      ) {
                        cancel.mutate(transfer);
                      }
                    }}
                  >
                    {t("lot.transfer.cancel", "Cancel transfer")}
                  </Button>
                  <Button size="sm" variant="primary" onClick={() => setReceiving(transfer)}>
                    {t("lot.transfer.receive", "Receive")}
                  </Button>
                </span>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[480px] text-[13px]">
                <thead className="text-left text-[11px] uppercase tracking-wide text-ink-muted">
                  <tr>
                    <th className="pb-1 font-medium">{t("lot.component.code", "Component")}</th>
                    <th className="pb-1 font-medium">{t("column.bom.component", "Part")}</th>
                    <th className="pb-1 text-right font-medium">{t("lot.transfer.shipped", "Shipped")}</th>
                    <th className="pb-1 text-right font-medium">{t("lot.transfer.received", "Received")}</th>
                    <th className="pb-1 pl-3 font-medium">{t("column.note", "Note")}</th>
                  </tr>
                </thead>
                <tbody>
                  {transfer.lines.map((line) => {
                    const short = line.quantity_received != null && Number(line.quantity_received) < Number(line.quantity_shipped);
                    return (
                      <tr key={line.id} className="border-t border-line">
                        <td className="py-1 font-mono text-[12px]">
                          {line.component_code} {line.letter && <span className="ml-1 font-sans text-ink-muted">{line.letter}</span>}
                        </td>
                        <td className="py-1">
                          {line.part_number} <span className="text-[11px] text-ink-faint">{line.part_name}</span>
                        </td>
                        <td className="py-1 text-right tabular-nums">{formatNumber(line.quantity_shipped)}</td>
                        <td className={`py-1 text-right tabular-nums ${short ? "text-risk" : ""}`}>
                          {line.quantity_received == null ? "" : formatNumber(line.quantity_received)}
                        </td>
                        <td className="py-1 pl-3 text-ink-muted">{line.note}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {transfer.note && <p className="mt-2 text-[12px] text-ink-muted">{transfer.note}</p>}
          </Card>
        ))
      )}
      {shipping && (
        <ShipDialog
          lot={lot}
          components={components}
          onClose={() => setShipping(false)}
          onShipped={async () => {
            setShipping(false);
            await transfers.refetch();
            await onChanged();
          }}
        />
      )}
      {receiving && (
        <ReceiveTransferDialog
          lotId={lot.id}
          lotNumber={lot.number}
          transfer={receiving}
          onClose={() => setReceiving(null)}
          onReceived={async () => {
            setReceiving(null);
            await transfers.refetch();
            await onChanged();
          }}
        />
      )}
    </div>
  );
}

export function transferStatusLabel(t: (key: string, fallback: string) => string, status: ProductionTransferStatus): string {
  return {
    SHIPPED: t("lot.transfer.status.shipped", "On its way"),
    RECEIVED: t("lot.transfer.status.received", "Received"),
    CANCELLED: t("lot.transfer.status.cancelled", "Cancelled"),
  }[status];
}

/** A truckload: each component with how many are loaded, starting at what it can still ship. */
function ShipDialog({
  lot,
  components,
  onClose,
  onShipped,
}: {
  lot: ProductionOrderDTO;
  components: ProductionOrderComponentDTO[];
  onClose: () => void;
  onShipped: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const confirm = useConfirm();
  const client = useMemo(() => new ProductionTransferClient(http), [http]);
  const [quantities, setQuantities] = useState<Record<number, string> | null>(null);
  const [truck, setTruck] = useState("");
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");

  const ready = useQuery({
    queryKey: ["production", "production-transfer", "ready", lot.id],
    queryFn: () => client.ready(lot.id),
  });
  // Prefilled once, from what each component can still ship.
  const values: Record<number, string> =
    quantities ??
    Object.fromEntries(components.map((component) => [component.id, String(Number(ready.data?.[String(component.id)] ?? 0))]));

  const lines = components
    .map((component) => ({ component, quantity: Number(values[component.id] ?? 0) }))
    .filter((line) => line.quantity > 0);
  const valid = lines.length > 0 && Object.values(values).every((value) => value.trim() === "" || Number(value) >= 0);

  const ship = useMutation({
    mutationFn: async () => {
      const transfer = {
        id: 0,
        number: 0,
        production_order_id: lot.id,
        from_site_id: 0,
        to_site_id: 0,
        status: "SHIPPED" as const,
        shipped_date: date || null,
        truck_reference: truck.trim() === "" ? null : truck.trim(),
        note: note.trim() === "" ? null : note.trim(),
        lines: lines.map((line) => ({
          id: 0,
          production_transfer_id: 0,
          production_order_component_id: line.component.id,
          quantity_shipped: line.quantity,
          quantity_received: null,
          note: null,
        })),
      };
      try {
        return await client.ship(transfer);
      } catch (failure) {
        const question = needsConfirmation(failure);
        if (question === null) throw failure;
        const yes = await confirm({
          title: t("lot.transfer.confirm-over.title", "Ship more than is finished?"),
          message: question,
          confirmLabel: t("lot.transfer.ship", "Ship to Terrebonne"),
        });
        if (!yes) return null;
        return client.shipConfirmed(transfer);
      }
    },
    onSuccess: async (shipped) => {
      if (!shipped) return;
      toast.success(t("lot.transfer.shipped-toast", "Transfer {{n}} on its way", { n: shipped.number }));
      await onShipped();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  return (
    <Dialog
      open
      size="lg"
      onClose={onClose}
      title={t("lot.transfer.ship", "Ship to Terrebonne")}
      description={t("lot.transfer.ship.description", "Each component starts at what is finished at Laval and not shipped yet.")}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={!valid} loading={ship.isPending} onClick={() => ship.mutate()}>
            {t("lot.transfer.ship-n", "Ship {{n}} components", { n: lines.length })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={t("lot.transfer.date", "Shipped on")} htmlFor="ship-date">
            <Input id="ship-date" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          </Field>
          <Field label={t("lot.transfer.truck", "Truck")} htmlFor="ship-truck">
            <Input id="ship-truck" value={truck} onChange={(event) => setTruck(event.target.value)} />
          </Field>
          <Field label={t("column.note", "Note")} htmlFor="ship-note">
            <Input id="ship-note" value={note} onChange={(event) => setNote(event.target.value)} />
          </Field>
        </div>
        <div className="max-h-[50vh] overflow-auto rounded-md border border-line">
          <table className="w-full text-[13px]">
            <thead className="sticky top-0 bg-surface-sunk text-left text-[11px] uppercase tracking-wide text-ink-muted">
              <tr>
                <th className="px-3 py-1.5 font-medium">{t("lot.component.code", "Component")}</th>
                <th className="px-3 py-1.5 text-right font-medium">{t("lot.transfer.ready", "Ready")}</th>
                <th className="px-3 py-1.5 font-medium">{t("lot.transfer.loaded", "Loaded")}</th>
              </tr>
            </thead>
            <tbody>
              {components.map((component) => (
                <tr key={component.id} className="border-t border-line">
                  <td className="px-3 py-1">
                    <span className="font-mono text-[12px]">{component.code}</span>
                    {component.letter && <span className="ml-2 text-ink-muted">{component.letter}</span>}
                    <span className="ml-2 text-[11px] text-ink-faint">{component.part_number}</span>
                  </td>
                  <td className="px-3 py-1 text-right tabular-nums text-ink-muted">{formatNumber(ready.data?.[String(component.id)] ?? 0)}</td>
                  <td className="px-3 py-1">
                    <Input
                      aria-label={t("lot.transfer.loaded", "Loaded")}
                      type="number"
                      min="0"
                      step="any"
                      className="h-7 w-24 py-0"
                      value={values[component.id] ?? ""}
                      onChange={(event) => setQuantities({ ...values, [component.id]: event.target.value })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Dialog>
  );
}
